import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, PLAN_LABELS, SQUARE_PAYMENT_URL } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CreditCard, CheckCircle2, Clock, Home, Calendar, Camera, X, Lock, Pencil, CalendarDays, CalendarRange, CalendarClock, Calendar as CalendarIcon, Copy, Check, Gift, Users, User, Info } from "lucide-react";
import { resizeImageToBase64 } from "../lib/image";

const PLANS = [
  { id: "daily", label: "Diario", desc: "Un poquito cada día", icon: CalendarDays },
  { id: "weekly", label: "Semanal", desc: "Cada 7 días", icon: CalendarRange },
  { id: "biweekly", label: "Quincenal", desc: "Cada 2 semanas", icon: CalendarClock },
  { id: "monthly", label: "Mensual", desc: "Una vez al mes", icon: CalendarIcon },
];

export default function MyAccount() {
  const { id } = useParams();
  const nav = useNavigate();
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingInstallment, setPayingInstallment] = useState(null);
  // PIN-gated edit mode
  const [pin, setPin] = useState("");
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [verifyingPin, setVerifyingPin] = useState(false);
  // Photo
  const [photoLoading, setPhotoLoading] = useState(false);
  // Plan edit
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [newPlan, setNewPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  // Coupons
  const [coupons, setCoupons] = useState([]);
  // Payment method modal
  const [showPayMethodModal, setShowPayMethodModal] = useState(false);
  const [pendingInstallmentNum, setPendingInstallmentNum] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({ stripe_enabled: false, square_enabled: true });

  const load = async () => {
    try {
      const { data } = await api.get(`/registrations/${id}`);
      setReg(data);
      // Load payment methods status
      try {
        const m = await api.get("/payments/methods");
        setPaymentMethods(m.data);
      } catch { /* fine */ }
      // Best-effort coupon fetch (public; no PIN needed)
      try {
        const c = await api.get(`/coupons/by-registration/${id}`);
        setCoupons(c.data.coupons || []);
      } catch { /* no coupons is fine */ }
    } catch (e) {
      toast.error("Inscripción no encontrada");
      nav("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const payInstallment = async (installment_number) => {
    setPayingInstallment(installment_number);
    setPendingInstallmentNum(installment_number);
    setShowPayMethodModal(true);
  };

  const payWithStripe = async (installment_number) => {
    setShowPayMethodModal(false);
    try {
      const { data } = await api.post("/payments/checkout", {
        registration_id: id,
        installment_number,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("No se pudo iniciar el pago");
        setPayingInstallment(null);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 503) {
        // Stripe disabled — gracefully fallback to Square
        toast.error(detail || "Pago con tarjeta temporalmente no disponible. Usa Square.", { duration: 9000 });
        setTimeout(() => payWithSquare(), 1500);
        return;
      }
      toast.error(detail || "Error al iniciar pago");
      setPayingInstallment(null);
    }
  };

  const payWithSquare = () => {
    setShowPayMethodModal(false);
    // Pre-fill reference and open Square
    const ref = `Campista ${reg?.camper_number_display || ""} - ${reg?.full_name || ""}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(ref).catch(() => {});
    }
    toast.success("Referencia copiada — pégala en la nota del pago de Square", { duration: 8000 });
    setTimeout(() => {
      window.location.href = SQUARE_PAYMENT_URL;
    }, 800);
  };

  const verifyPin = async () => {
    if (!pin || pin.length !== 4) {
      toast.error("Tu PIN son los últimos 4 dígitos de tu teléfono");
      return;
    }
    setVerifyingPin(true);
    try {
      await api.post(`/registrations/${id}/verify-pin`, { pin });
      setEditUnlocked(true);
      setShowPinModal(false);
      toast.success("Edición desbloqueada");
    } catch (e) {
      toast.error("PIN incorrecto. Son los últimos 4 dígitos de tu teléfono");
      setPin("");
    } finally {
      setVerifyingPin(false);
    }
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("La imagen es muy grande (máx 12MB)");
      return;
    }
    setPhotoLoading(true);
    try {
      const dataUrl = await resizeImageToBase64(file, 400, 0.85);
      const { data } = await api.patch(`/registrations/${id}/photo`, { pin, photo: dataUrl });
      setReg(data);
      toast.success("Foto actualizada");
    } catch (err) {
      const msg = err.response?.data?.detail || err?.message || "No se pudo actualizar la foto";
      toast.error(msg, { duration: 8000 });
    } finally {
      setPhotoLoading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const removePhoto = async () => {
    setPhotoLoading(true);
    try {
      const { data } = await api.patch(`/registrations/${id}/photo`, { pin, photo: null });
      setReg(data);
      toast.success("Foto removida");
    } catch (err) {
      toast.error("No se pudo remover");
    } finally {
      setPhotoLoading(false);
    }
  };

  const savePlan = async () => {
    if (!newPlan || newPlan === reg.plan_type) {
      setShowPlanModal(false);
      return;
    }
    setSavingPlan(true);
    try {
      const { data } = await api.patch(`/registrations/${id}/plan`, { pin, plan_type: newPlan });
      setReg(data);
      toast.success(`Plan cambiado a ${PLAN_LABELS[newPlan]}`);
      setShowPlanModal(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo cambiar el plan";
      toast.error(msg);
    } finally {
      setSavingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!reg) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50 py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => nav("/")} data-testid="myaccount-home-button">
              <Home className="w-4 h-4 mr-2" /> Inicio
            </Button>
            <Button
              variant="ghost"
              onClick={() => nav("/info")}
              data-testid="myaccount-info-button"
              className="text-yellow-700 hover:text-yellow-800 hover:bg-yellow-100"
            >
              <Info className="w-4 h-4 mr-2" /> Info del campamento
            </Button>
          </div>
          <SessionSwitcher currentId={id} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header with photo */}
          <div className="bg-black p-8 text-white">
            <div className="flex items-start gap-5">
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 rounded-full border-4 border-yellow-400 bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center overflow-hidden shadow-lg">
                  {reg.photo ? (
                    <img src={reg.photo} alt="foto" className="w-full h-full object-cover" />
                  ) : photoLoading ? (
                    <Loader2 className="w-7 h-7 text-orange-500 animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-orange-500" />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-yellow-400 mb-2">Mi inscripción</div>
                <h1 className="font-heading uppercase text-3xl sm:text-4xl break-words">{reg.full_name}</h1>
                <div className="text-white/60 text-sm mt-2">
                  {reg.age_category === "adult" ? "Adulto" : reg.age_category === "child" ? "Niño" : "3-6 años"} · Plan {PLAN_LABELS[reg.plan_type]}
                </div>
                {reg.camper_number_display && (
                  <CamperNumberBadge
                    full_name={reg.full_name}
                    number_display={reg.camper_number_display}
                  />
                )}
                {coupons.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {coupons.map((c) => (
                      <CouponBanner key={c.id} coupon={c} onOpen={() => nav(`/cupon/${c.code}`)} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Edit mode controls */}
            <div className="mt-6 flex flex-wrap gap-2">
              {!editUnlocked ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowPinModal(true)}
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-yellow-400"
                  data-testid="unlock-edit-button"
                >
                  <Lock className="w-3 h-3 mr-2" /> Editar mi inscripción
                </Button>
              ) : (
                <>
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-black rounded-md cursor-pointer text-xs font-semibold uppercase tracking-wider">
                    <Camera className="w-3 h-3" />
                    {reg.photo ? "Cambiar foto" : "Subir foto"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onPickPhoto}
                      className="hidden"
                      data-testid="myaccount-input-photo"
                    />
                  </label>
                  {reg.photo && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={removePhoto}
                      disabled={photoLoading}
                      className="bg-white/10 border-white/20 text-white hover:bg-red-500 hover:border-red-500"
                      data-testid="myaccount-remove-photo"
                    >
                      <X className="w-3 h-3 mr-1" /> Quitar foto
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setNewPlan(reg.plan_type); setShowPlanModal(true); }}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-yellow-400"
                    data-testid="myaccount-edit-plan"
                  >
                    <Pencil className="w-3 h-3 mr-2" /> Cambiar plan
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Progress */}
          <div className="p-8 border-b">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Tu progreso</div>
                <div className="font-heading text-5xl text-gradient-gold">{reg.percentage_paid}%</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Pagado <strong>${reg.paid_amount}</strong></div>
                <div className="text-sm text-gray-600">Restante <strong className="text-orange-600">${reg.remaining_amount}</strong></div>
                <div className="text-xs text-gray-500 mt-1">Meta: ${reg.total_amount}</div>
              </div>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${reg.percentage_paid}%` }}
                transition={{ duration: 1.2 }}
                className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
              />
            </div>
          </div>

          {/* Next installment highlight */}
          {reg.next_installment && (
            <div className="p-8 bg-orange-50 border-b border-orange-100" data-testid="next-installment">
              <div className="text-xs uppercase tracking-widest text-orange-700 font-bold mb-2">Siguiente cuota</div>
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <div>
                  <div className="font-heading text-5xl text-black">${reg.next_installment.amount}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    Vence el {new Date(reg.next_installment.due_date).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={() => payInstallment(reg.next_installment.installment_number)}
                  disabled={payingInstallment === reg.next_installment.installment_number}
                  data-testid="pay-next-installment-button"
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold uppercase tracking-wider px-8"
                >
                  {payingInstallment === reg.next_installment.installment_number ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <><CreditCard className="w-4 h-4 mr-2" /> Pagar cuota</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Schedule */}
          <div className="p-8">
            <h2 className="font-heading uppercase text-2xl text-black mb-4">Calendario de cuotas</h2>
            <div className="space-y-2" data-testid="installments-schedule">
              {reg.schedule?.map((s) => {
                const isPaid = s.status === "paid";
                const isNext = !isPaid && reg.next_installment?.installment_number === s.installment_number;
                return (
                  <div
                    key={s.installment_number}
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                      isPaid
                        ? "bg-green-50 border-green-200"
                        : isNext
                        ? "bg-orange-50 border-orange-300 ring-2 ring-orange-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                    data-testid={`installment-row-${s.installment_number}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isPaid ? "bg-green-500 text-white" : isNext ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-600"
                      }`}>
                        {isPaid ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-4 h-4" />}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">Cuota #{s.installment_number}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(s.due_date).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-heading text-xl ${isPaid ? "text-green-600" : "text-black"}`}>${s.amount}</div>
                      {isPaid && <div className="text-xs text-green-600 font-semibold">Pagada</div>}
                    </div>
                    {!isPaid && !isNext && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => payInstallment(s.installment_number)}
                        disabled={!!payingInstallment}
                        className="ml-3"
                        data-testid={`pay-installment-${s.installment_number}`}
                      >
                        {payingInstallment === s.installment_number ? <Loader2 className="w-3 h-3 animate-spin" /> : "Pagar"}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {reg.percentage_paid >= 100 && (
            <div className="p-8 bg-gradient-to-br from-green-500 to-emerald-600 text-white text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4" />
              <h2 className="font-heading uppercase text-4xl mb-2">¡Todo pagado!</h2>
              <p className="text-white/90">Nos vemos el 28 de agosto, prepárate para Operación Rescate.</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* PIN Modal */}
      <AnimatePresence>
        {showPinModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-6"
            onClick={() => setShowPinModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
              data-testid="pin-modal"
            >
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                  <Lock className="w-7 h-7 text-black" />
                </div>
              </div>
              <h3 className="font-heading uppercase text-2xl text-black text-center mb-2">Confirma tu PIN</h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Tu PIN son los <strong>últimos 4 dígitos</strong> del teléfono con el que te inscribiste.
              </p>
              <Label htmlFor="pin">PIN</Label>
              <Input
                id="pin"
                data-testid="pin-input"
                type="tel"
                maxLength={4}
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                onKeyDown={(e) => e.key === "Enter" && verifyPin()}
                placeholder="••••"
                className="mt-2 h-14 text-center text-3xl tracking-[1em] font-bold"
                autoFocus
              />
              <div className="grid grid-cols-2 gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => { setShowPinModal(false); setPin(""); }}
                  data-testid="pin-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={verifyPin}
                  disabled={verifyingPin || pin.length !== 4}
                  data-testid="pin-confirm"
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {verifyingPin ? <Loader2 className="w-4 h-4 animate-spin" /> : "Desbloquear"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plan change modal */}
      <AnimatePresence>
        {showPlanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-6"
            onClick={() => setShowPlanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
              data-testid="plan-modal"
            >
              <h3 className="font-heading uppercase text-2xl text-black mb-2">Cambiar plan de pago</h3>
              <p className="text-sm text-gray-600 mb-5">
                Las cuotas que ya pagaste se mantienen. El monto restante (<strong>${reg.remaining_amount}</strong>) se reorganiza con el nuevo plan.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {PLANS.map((p) => {
                  const isActive = newPlan === p.id;
                  const isCurrent = reg.plan_type === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setNewPlan(p.id)}
                      data-testid={`plan-option-${p.id}`}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isActive
                          ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p.icon className="w-5 h-5 text-orange-500" />
                        {isCurrent && (
                          <span className="text-[9px] uppercase tracking-widest bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-bold">Actual</span>
                        )}
                      </div>
                      <div className="font-heading uppercase text-lg text-black">{p.label}</div>
                      <div className="text-xs text-gray-500">{p.desc}</div>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowPlanModal(false)}
                  data-testid="plan-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={savePlan}
                  disabled={savingPlan || !newPlan || newPlan === reg.plan_type}
                  data-testid="plan-confirm"
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {savingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambio"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Method Selection Modal */}
      <AnimatePresence>
        {showPayMethodModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => { setShowPayMethodModal(false); setPayingInstallment(null); }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            data-testid="pay-method-modal"
          >
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-zinc-900 border-t-2 sm:border-2 border-yellow-400/50 rounded-t-3xl sm:rounded-3xl w-full max-w-lg p-6 sm:p-8"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300 font-bold">Cuota #{pendingInstallmentNum}</div>
                  <h2 className="text-2xl sm:text-3xl font-black text-yellow-100 mt-1">Elige cómo pagar</h2>
                </div>
                <button
                  onClick={() => { setShowPayMethodModal(false); setPayingInstallment(null); }}
                  className="text-white/50 hover:text-white p-1"
                  data-testid="pay-method-close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Stripe option (only if live key is configured) */}
              {paymentMethods.stripe_enabled && (
                <button
                  onClick={() => payWithStripe(pendingInstallmentNum)}
                  data-testid="pay-with-stripe-btn"
                  className="w-full mt-6 group bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 hover:from-purple-700 hover:via-indigo-700 hover:to-blue-700 rounded-2xl p-5 text-left transition-all shadow-xl hover:shadow-2xl"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-bold mb-1">
                        Recomendado
                      </div>
                      <div className="text-white font-black text-lg sm:text-xl">
                        Pago Instantáneo
                      </div>
                      <div className="text-white/80 text-xs sm:text-sm mt-1">
                         Apple Pay · Google Pay · Cash App · Tarjeta
                      </div>
                      <div className="text-green-300 text-xs font-bold mt-1.5">
                        ✓ Se asigna automáticamente
                      </div>
                    </div>
                    <div className="ml-3 w-12 h-12 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </button>
              )}

              {/* Square option */}
              <button
                onClick={() => payWithSquare()}
                data-testid="pay-with-square-btn"
                className="w-full mt-3 group bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-yellow-400/40 rounded-2xl p-5 text-left transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-yellow-200 font-bold text-base sm:text-lg">
                      Pagar con Square
                    </div>
                    <div className="text-white/60 text-xs sm:text-sm mt-1">
                      Si ya tienes tarjeta guardada en Square
                    </div>
                    <div className="text-amber-300/90 text-[11px] mt-1.5 font-medium">
                      ⚠ Requiere pegar tu referencia en la nota
                    </div>
                  </div>
                  <div className="ml-3 w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-yellow-300" />
                  </div>
                </div>
              </button>

              {/* Reference banner for Square */}
              {reg?.camper_number_display && (
                <div className="mt-4 rounded-xl border border-amber-400/40 bg-amber-500/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">
                    Si eliges Square, copia esto en la NOTA del pago:
                  </div>
                  <div className="font-mono text-amber-100 text-sm font-bold break-all">
                    Campista {reg.camper_number_display} - {reg.full_name}
                  </div>
                </div>
              )}

              <p className="mt-5 text-center text-white/40 text-[11px]">
                Operación Rescate · Pagos seguros
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


function CamperNumberBadge({ full_name, number_display }) {
  const [copied, setCopied] = useState(false);
  const reference = `Campista ${number_display} - ${full_name}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      toast.success("Referencia copiada");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback: select-all via prompt
      window.prompt("Copia esta referencia:", reference);
    }
  };
  return (
    <div className="mt-4 rounded-2xl border border-yellow-400/40 bg-gradient-to-br from-yellow-500/15 via-yellow-500/5 to-transparent p-4 backdrop-blur-sm" data-testid="camper-number-badge">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/80 font-bold mb-1">
            Tu número de campista
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-4xl sm:text-5xl text-gradient-gold leading-none tracking-tight" data-testid="camper-number-value">
              {number_display}
            </span>
            <span className="text-yellow-100/60 text-xs uppercase tracking-widest">de Op. Rescate</span>
          </div>
          <p className="mt-2 text-xs text-yellow-100/70">
            Usa esta referencia al pagar (efectivo, Square o transferencia) para que tu pago se asocie automáticamente.
          </p>
        </div>
        <Button
          size="sm"
          onClick={copy}
          data-testid="camper-number-copy"
          className={`min-w-[120px] font-bold uppercase tracking-wider transition-colors ${
            copied
              ? "bg-green-500 hover:bg-green-500 text-white"
              : "bg-yellow-400 hover:bg-yellow-500 text-black"
          }`}
        >
          {copied ? (
            <><Check className="w-4 h-4 mr-1" /> Copiado</>
          ) : (
            <><Copy className="w-4 h-4 mr-1" /> Copiar</>
          )}
        </Button>
      </div>
    </div>
  );
}

function CouponBanner({ coupon, onOpen }) {
  const expired = coupon?.status === "expired";
  return (
    <div
      onClick={!expired ? onOpen : undefined}
      data-testid={`coupon-banner-${coupon.code}`}
      className={`rounded-2xl border-2 ${
        expired
          ? "border-red-700/40 bg-black/40 opacity-60 cursor-not-allowed"
          : "border-pink-400/60 bg-gradient-to-br from-pink-500/15 via-pink-500/5 to-yellow-500/5 cursor-pointer hover:border-pink-300 hover:shadow-[0_0_30px_-10px_rgba(236,72,153,0.6)]"
      } p-4 backdrop-blur-sm transition-all`}
    >
      <div className="flex items-center gap-4">
        <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center ${expired ? "bg-red-500/20" : "bg-pink-500/20 ring-2 ring-pink-300/50"}`}>
          <Gift className={`w-7 h-7 ${expired ? "text-red-300" : "text-pink-200"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-pink-200/80 font-bold mb-0.5">
            {expired ? "Cupón expirado" : "🎉 Tu cupón ganado"}
          </div>
          <div className="font-black text-lg leading-tight text-yellow-100 truncate">
            ${coupon.value} en Dulce Vida
          </div>
          <div className="font-mono text-xs text-yellow-300/90 mt-0.5">
            {coupon.code}
          </div>
        </div>
        {!expired && (
          <div className="flex-shrink-0 text-pink-200 text-xs font-bold uppercase tracking-wider">
            Abrir →
          </div>
        )}
      </div>
    </div>
  );
}


function SessionSwitcher({ currentId }) {
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("rescate_session");
      if (raw) setSession(JSON.parse(raw));
    } catch {}
  }, []);

  const logout = () => {
    if (!window.confirm("¿Cerrar sesión y volver al inicio?")) return;
    localStorage.removeItem("rescate_session");
    nav("/");
  };

  if (!session?.accounts?.length) {
    return (
      <Button variant="ghost" size="sm" onClick={logout} className="text-gray-500 text-xs" data-testid="myaccount-logout">
        Cerrar sesión
      </Button>
    );
  }

  const others = session.accounts.filter(a => a.id !== currentId);
  if (others.length === 0) {
    return (
      <Button variant="ghost" size="sm" onClick={logout} className="text-gray-500 text-xs" data-testid="myaccount-logout">
        Cerrar sesión
      </Button>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(o => !o)}
        data-testid="session-switcher-btn"
        className="bg-white border-2 border-yellow-300 hover:border-yellow-500 text-black"
      >
        <Users className="w-4 h-4 mr-2" />
        Cambiar cuenta ({session.accounts.length})
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white border-2 border-yellow-300 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="session-switcher-menu">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold px-3 pt-3">
            Tus inscripciones
          </div>
          <div className="p-2 space-y-1.5 max-h-80 overflow-y-auto">
            {session.accounts.map(a => {
              const isCurrent = a.id === currentId;
              return (
                <button
                  key={a.id}
                  onClick={() => {
                    setOpen(false);
                    if (!isCurrent) nav(`/mi-cuenta/${a.id}`);
                  }}
                  disabled={isCurrent}
                  data-testid={`switch-to-${a.id}`}
                  className={`w-full text-left p-2.5 rounded-lg flex items-center gap-2 ${isCurrent ? "bg-yellow-100 cursor-default" : "hover:bg-yellow-50 cursor-pointer"}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-300 to-orange-400 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-black" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-black truncate flex items-center gap-1.5">
                      {a.full_name}
                      {a.camper_number_display && (
                        <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-yellow-200 text-yellow-900">{a.camper_number_display}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {isCurrent ? "Viendo ahora" : "Cambiar a esta cuenta"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <button
            onClick={logout}
            data-testid="session-switcher-logout"
            className="w-full text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 py-2.5 border-t"
          >
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

